import { Calendar, MapPin, Users, Trophy, ChevronRight } from "lucide-react";
import { SeasonMatch } from "../../models/leagueInterfaces";
import { MatchSetupConfig } from "../../models/match";
import { useLeaderboardStore } from "../../store/leaderboardStore";
import { showDate } from "../../utils/dateUtils";

interface MatchesListProps {
  matches: SeasonMatch[];
  matchConfigs: MatchSetupConfig[];
}

export function Matches({ matches, matchConfigs }: MatchesListProps) {
  const { setLeaderboardMatchId, setLeaderboardLevel } = useLeaderboardStore();

  const getMatchStatus = (config?: MatchSetupConfig) => {
    if (!config) return "Not Started";
    if (config.isMatchCompleted) return "Completed";
    if (config.matchInProgress) return "In Progress";
    return "Not Started";
  };

  // Group matches into buckets
  const matchesByStatus = matches.reduce((acc, match) => {
    const config = matchConfigs.find((c) => c.matchId === match.id);
    const status = getMatchStatus(config);
    
    if (status === "In Progress") {
      acc.inProgress.push(match);
    } else if (status === "Completed") {
      acc.completed.push(match);
    } else {
      acc.notStarted.push(match);
    }
    return acc;
  }, {
    inProgress: [] as SeasonMatch[],
    completed: [] as SeasonMatch[],
    notStarted: [] as SeasonMatch[]
  });

  // Sort completed matches in reverse chronological order
  matchesByStatus.completed.sort((a, b) => 
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  // Sort not started matches in chronological order
  matchesByStatus.notStarted.sort((a, b) => 
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  // Combine all matches in the desired order
  const sortedMatches = [
    ...matchesByStatus.inProgress,
    ...matchesByStatus.completed,
    ...matchesByStatus.notStarted
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-white mt-3 sm:mt-0">
          Season Matches
        </h1>
        <button
          onClick={() => setLeaderboardLevel("season")}
          className=" text-white hover:underline cursor-pointer"
        >
          View Season Leaderboard
        </button>
      </div>

      <div className="grid gap-6">
        {sortedMatches &&
          sortedMatches.map((match) => {
            const config = matchConfigs.find((c) => c.matchId === match.id);
            const status = getMatchStatus(config);
            const showLeaderboard =
              config?.matchInProgress || config?.isMatchCompleted;

            return (
              <div
                key={match.id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden transition-colors duration-200"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {match.location} - {showDate(`${match.date}`)}
                    </h2>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        status === "Completed"
                          ? "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400"
                          : status === "In Progress"
                          ? "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400"
                          : "bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400"
                      }`}
                    >
                      {status}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <Calendar className="w-5 h-5 mr-2" />
                      <span>{match.teeOff}</span>
                    </div>
                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <MapPin className="w-5 h-5 mr-2" />
                      <span>{match.location}</span>
                    </div>
                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <Users className="w-5 h-5 mr-2" />
                      <span>{match.groupOrKnockout}</span>
                    </div>
                    <div className="flex items-center text-gray-600 dark:text-gray-400">
                      <Trophy className="w-5 h-5 mr-2" />
                      <span>{match.matchType}</span>
                    </div>
                  </div>

                  {showLeaderboard && (
                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={() => setLeaderboardMatchId(match.id)}
                        className="inline-flex items-center px-4 py-2 bg-green-600 dark:bg-green-700 text-white rounded-lg hover:bg-green-700 dark:hover:bg-green-800 transition-colors duration-200"
                      >
                        <span>View Leaderboard</span>
                        <ChevronRight className="w-5 h-5 ml-2" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
